import { ApiClient } from "@/utils/apiClient";
import type { ApiResponse } from "@/models/api";
import type {
  Membership,
  MembershipContributions,
} from "@/models/membership";
import type { GroupingInterval } from "@/models/misc";
import type { CardSystem, CardCurrencyCode } from "@/models/card";
import { getUTMParamsAsHeaders } from "../utils/utm";

export type MembershipContributionsGetPayload = {
  from: string;
  to: string;
  interval?: GroupingInterval;
};
export type MembershipContributionsGetResponse = ApiResponse<{
  contributions: MembershipContributions;
}>;
export type CreateMembershipPayload = {
  newsletter?: boolean;
  referralCode?: string;
  affiliateProgram?: string;
};
export type CreateMembershipResponse = ApiResponse<{
  membership: Membership;
}>;
export type UpdateMembershipPayload = {
  newsletter?: boolean;
  maxRoundUp?: number | null;
  over18?: boolean;
  membershipTierId?: number | null;
  membershipTierPlanId?: number | null;
  cardId?: number | null;
};
export type UpdateMembershipResponse = ApiResponse<{
  membership: Membership;
}>;
export type PreviewMembershipUpdatePayload = {
  membershipTierId?: number | null;
  membershipTierPlanId?: number | null;
};
export type PreviewMembershipUpdateResponse = ApiResponse<{
  amountToCharge: number;
}>;
export type CancelMembershipResponse = ApiResponse<{
  membership: Membership;
}>;
export type SaveMembershipAffinitiesPayload = {
  affinityIds: number[] | null;
};
export type SaveMembershipAffinitiesResponse = ApiResponse<{}>;
export type GetImportTokenResponse = ApiResponse<{
  membership: Membership;
}>;
export type ActivateImportedMembershipPayload = {
  password: string;
  cardName: string;
  cardSystem: CardSystem;
  cardCurrencyCode: CardCurrencyCode;
  cardPlatformData?: object;
};
export type ActivateImportedMembershipResponse = ApiResponse<{
  membership: Membership;
}>;
export type ChargeMembershipPayload = {
  cardId: number;
};
export type ChargeMembershipResponse = ApiResponse<{
  membership: Membership;
}>;

export abstract class MembershipApiService {
  public static async getContributions(payload: MembershipContributionsGetPayload): Promise<MembershipContributionsGetResponse> {
    const response = await ApiClient.getClient().get<MembershipContributionsGetResponse>('/membership/contributions', {
      params: payload,
    });
    return response.data;
  }

  public static async create(payload: CreateMembershipPayload): Promise<CreateMembershipResponse> {
    const response = await ApiClient.getClient().post<CreateMembershipResponse>(
      '/membership',
      payload,
      {
        headers: {
          ...getUTMParamsAsHeaders(),
        },
      },
    );
    return response.data;
  }

  public static async update(payload: UpdateMembershipPayload): Promise<UpdateMembershipResponse> {
    const response = await ApiClient.getClient().patch<UpdateMembershipResponse>('/membership', payload);
    return response.data;
  }

  public static async updatePreview(payload: PreviewMembershipUpdatePayload): Promise<PreviewMembershipUpdateResponse> {
    const response = await ApiClient.getClient().get<PreviewMembershipUpdateResponse>('/membership/update-preview', {
      params: payload,
    });
    return response.data;
  }

  public static async charge(payload: ChargeMembershipPayload): Promise<ChargeMembershipResponse> {
    const response = await ApiClient.getClient().post<ChargeMembershipResponse>('/membership/charge', payload);
    return response.data;
  }

  public static async cancel(): Promise<CancelMembershipResponse> {
    const response = await ApiClient.getClient().post<CancelMembershipResponse>('/membership/cancel');
    return response.data;
  }

  public static async saveAffinities(payload: SaveMembershipAffinitiesPayload): Promise<SaveMembershipAffinitiesResponse> {
    const response = await ApiClient.getClient().post<SaveMembershipAffinitiesResponse>('/membership/affinities', payload);
    return response.data;
  }

  public static async getImportToken(token: string): Promise<GetImportTokenResponse> {
    const response = await ApiClient.getClient().get<GetImportTokenResponse>('/activate/' + token);
    return response.data;
  }

  public static async activateImportedMembership(token: string, payload: ActivateImportedMembershipPayload): Promise<ActivateImportedMembershipResponse> {
    const response = await ApiClient.getClient().post<ActivateImportedMembershipResponse>('/activate/' + token, payload);
    return response.data;
  }
}
